/**
 * Maps an `AiJob` onto what the /generation/:jobId screen renders: a 0..1
 * fraction for the progress bar and a 0..3 index into the four displayed
 * step rows (Écriture / Relecture / Couverture / Voix).
 *
 * Backend contract (revised 2026-09-20 — the cover is now generated
 * *before* the audio, so the generation screen can show it during the
 * remaining wait instead of a placeholder):
 *   - `progress_label` ∈ "guard" | "writing" | "review" | "cover" | "voice"
 *     — note the step order: cover now comes before voice.
 *   - `progress_total` = 100
 *   - `progress_done` = an integer 0..100, weighted by step and monotonic.
 *     Start-of-step milestones: guard 0, writing 3, review 28, cover 40,
 *     voice 48, end 100. Only "voice" (the longest step, ~52 points) is
 *     interpolated continuously by the backend as TTS chunks land — every
 *     other step, "cover" included, sits flat at its milestone value for
 *     its whole duration (e.g. "writing" can hold at 3 for ~35s with zero
 *     intermediate events).
 *
 * "guard" (a ~2s content-safety pass before writing starts) is folded into
 * the same displayed row as "writing" — it doesn't deserve its own line,
 * and merging it keeps the four existing i18n step keys unchanged.
 */
import type { AiJob } from "./types";

export const STEP_COUNT = 4;

const KNOWN_LABELS = ["guard", "writing", "review", "cover", "voice"] as const;
type KnownLabel = (typeof KNOWN_LABELS)[number];

/** Displayed step index (0..3) for each known `progress_label`. */
const STEP_INDEX_BY_LABEL: Record<KnownLabel, number> = {
  guard: 0,
  writing: 0,
  review: 1,
  cover: 2,
  voice: 3,
};

/**
 * Fraction (0..1) of the *start* of each displayed step, in step order —
 * mirrors the backend's milestones above, expressed as fractions instead
 * of 0..100 integers. Used by the screen to make the progress bar "creep"
 * toward the next milestone during long silences without ever reaching or
 * passing it.
 */
export const PROGRESS_MILESTONES: readonly number[] = [0, 0.03, 0.28, 0.4, 0.48, 1];

function isKnownLabel(label: string | null | undefined): label is KnownLabel {
  return !!label && (KNOWN_LABELS as readonly string[]).includes(label);
}

function toFiniteNumber(n: number | null | undefined): number {
  return typeof n === "number" && Number.isFinite(n) ? n : 0;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

export interface JobProgressInput {
  status?: AiJob["status"] | string | null;
  progress_label?: string | null;
  progress_done?: number | null;
  progress_total?: number | null;
}

export interface JobProgress {
  /** 0..1, clamped, always finite. */
  fraction: number;
  /** 0..3, or -1 when nothing is known yet. */
  stepIndex: number;
}

/** Smallest fraction strictly greater than `fraction` in `PROGRESS_MILESTONES`, or 1 if already at/past the last one. */
export function nextMilestoneAbove(fraction: number): number {
  for (const m of PROGRESS_MILESTONES) {
    if (m > fraction) return m;
  }
  return 1;
}

/**
 * One "creep" tick: eases `current` toward `ceiling` by closing `rate` of
 * the remaining gap, without ever reaching or passing it. Asymptotic by
 * construction (a fixed fraction of a shrinking gap), so `current` only
 * ever *approaches* `ceiling` — see Generation.tsx for how `rate` is
 * calibrated against the pipeline's longest silence.
 */
export function creepStep(current: number, ceiling: number, rate: number): number {
  if (current >= ceiling) return current;
  return Math.min(current + (ceiling - current) * rate, ceiling);
}

/**
 * Pure mapping from an `AiJob` to what the generation screen renders.
 * Never throws — degenerate/missing input just yields "nothing known yet".
 */
export function computeJobProgress(job: JobProgressInput | null | undefined): JobProgress {
  if (!job) return { fraction: 0, stepIndex: -1 };

  if (job.status === "done") {
    return { fraction: 1, stepIndex: STEP_COUNT - 1 };
  }

  if (isKnownLabel(job.progress_label)) {
    const done = toFiniteNumber(job.progress_done);
    return { fraction: clamp01(done / 100), stepIndex: STEP_INDEX_BY_LABEL[job.progress_label] };
  }

  // --- Legacy fallback, meant to disappear ----------------------------
  // A backend that hasn't been redeployed with the contract above yet
  // sends free-form French prose in `progress_label` (e.g. "Étape 2/5 —
  // Écriture de l'histoire…") and a small 0..N step counter in
  // `progress_done`/`progress_total`, instead of the weighted 0..100
  // percentage handled above. This branch degrades gracefully against
  // that: fraction from the raw counter, step index from `done - 1`
  // (clamped). Delete once every environment speaks the new contract.
  const total = toFiniteNumber(job.progress_total);
  const done = toFiniteNumber(job.progress_done);
  const fraction = total > 0 ? clamp01(done / total) : 0;
  const stepIndex = total > 0 ? Math.max(0, Math.min(STEP_COUNT - 1, Math.floor(done) - 1)) : -1;
  return { fraction, stepIndex };
}

/**
 * "Sticky" story title for the generation screen: once a poll reply carries
 * a non-empty `result.title` (published mid-run, during "voice" — see
 * `AiJob.result` in `types.ts`), it is kept forever, even if a later reply
 * — out of order, or from a backend that only attaches it near the very end
 * — comes back without one. Same monotonic principle as the progress bar
 * and step checklist in Generation.tsx: once shown, a fact about this job
 * never un-shows itself.
 */
export function stickyTitle(current: string | undefined, incoming: string | null | undefined): string | undefined {
  return incoming && incoming.trim().length > 0 ? incoming : current;
}

export interface CoverFetchInput {
  result?: { cover_ready?: boolean | null } | null;
}

/**
 * Whether the generation screen should fire its one-shot `GET
 * /chouette/stories/{id}` to pick up the freshly-written `cover_svg`.
 *
 * The image itself never rides the 3s poll (that would mean re-downloading
 * an SVG on every tick for the rest of the job) — `result.cover_ready`
 * is only ever a signal to fetch the story *once*. So this is true on
 * exactly the first poll reply where `cover_ready` is `true`, given the
 * caller tracks `alreadyFetched`/`alreadyHaveCover` and flips it before
 * the next call — never on a later tick, even if `cover_ready` keeps
 * coming back `true` (harmless to ask again, but pointless), and never
 * once a cover image is already held, even if a later reply arrives
 * without `cover_ready` (best-effort generation: absent doesn't mean the
 * existing cover is wrong, so nothing is ever un-shown — see the
 * `Cover.tsx` usage in `Generation.tsx`).
 */
export function shouldFetchCover(
  job: CoverFetchInput | null | undefined,
  state: { alreadyFetched: boolean; alreadyHaveCover: boolean },
): boolean {
  if (state.alreadyFetched || state.alreadyHaveCover) return false;
  return job?.result?.cover_ready === true;
}
